import type { NotificationType, SmsTwilioCredentialsInput } from '@today/types'
import { FieldShell } from '@today/ui-components'
import { Button, Dialog } from '@today/ui-primitives'
import type { ChangeEvent } from 'react'
import {
	notificationTypeLabel,
	SMS_SELECTABLE_NOTIFICATION_TYPES,
	type SmsSettingsDraft,
	type SmsSettingsFieldErrors,
} from './sms-settings-draft'

// The field groups the SMS settings form is built from (TO-383 REQ-014): each
// looks its error up by the API's field path, so a `details.field` refusal
// lands next to the field that caused it.

const INPUT_CLASS =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring disabled:cursor-not-allowed disabled:opacity-50'

interface InputProps {
	label: string
	path: string
	errors: SmsSettingsFieldErrors
	value: string
	onChange: (value: string) => void
	type?: 'text' | 'password' | 'number'
	description?: string
}

function SettingsInput({ label, path, errors, value, onChange, type = 'text', description }: InputProps) {
	return (
		<FieldShell label={label} description={description} error={errors[path]}>
			<input
				type={type}
				autoComplete="off"
				className={INPUT_CLASS}
				value={value}
				onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
			/>
		</FieldShell>
	)
}

/** An error that belongs to a group of controls rather than one input. */
export function GroupError({ path, errors }: { path: string; errors: SmsSettingsFieldErrors }) {
	const message = errors[path]
	return message ? <p className="text-ds-sm text-ds-danger">{message}</p> : null
}

interface CredentialsProps {
	credentials: SmsTwilioCredentialsInput
	hasCredentials: boolean
	errors: SmsSettingsFieldErrors
	onChange: (credentials: SmsTwilioCredentialsInput) => void
}

/** The tenant's Twilio API-key trio: write-only, never prefilled. */
export function CredentialFields({ credentials, hasCredentials, errors, onChange }: CredentialsProps) {
	const set = (patch: Partial<SmsTwilioCredentialsInput>) => onChange({ ...credentials, ...patch })
	return (
		<fieldset className="flex flex-col gap-3">
			<legend className="text-ds-sm font-semibold text-ds-text">Twilio credentials</legend>
			{hasCredentials ? (
				<p className="text-ds-sm text-ds-text-muted">
					Credentials stored. Leave the fields empty to keep them, or fill all three to replace them.
				</p>
			) : null}
			<GroupError path="credentials" errors={errors} />
			<SettingsInput
				label="Account SID"
				path="credentials.accountSid"
				errors={errors}
				value={credentials.accountSid}
				onChange={(accountSid) => set({ accountSid })}
			/>
			<SettingsInput
				label="API Key SID"
				path="credentials.apiKeySid"
				errors={errors}
				value={credentials.apiKeySid}
				onChange={(apiKeySid) => set({ apiKeySid })}
			/>
			<SettingsInput
				label="API Key Secret"
				path="credentials.apiKeySecret"
				type="password"
				errors={errors}
				value={credentials.apiKeySecret}
				onChange={(apiKeySecret) => set({ apiKeySecret })}
			/>
		</fieldset>
	)
}

interface TypesProps {
	types: NotificationType[] | null
	errors: SmsSettingsFieldErrors
	onChange: (types: NotificationType[] | null) => void
}

/** "All types" (`null`) or an explicit allowlist of notification types. */
function NotificationTypeFields({ types, errors, onChange }: TypesProps) {
	const toggle = (type: NotificationType, checked: boolean) => {
		const current = types ?? []
		onChange(checked ? [...current, type] : current.filter((entry) => entry !== type))
	}
	return (
		<fieldset className="flex flex-col gap-2">
			<legend className="text-ds-sm font-semibold text-ds-text">Notification types sent by SMS</legend>
			<GroupError path="smsNotificationTypes" errors={errors} />
			<label className="flex items-center gap-2 text-ds-sm text-ds-text">
				<input
					type="checkbox"
					checked={types === null}
					onChange={(event) => onChange(event.target.checked ? null : [])}
				/>
				All types
			</label>
			{types === null
				? null
				: SMS_SELECTABLE_NOTIFICATION_TYPES.map((type) => (
						<label key={type} className="flex items-center gap-2 pl-6 text-ds-sm text-ds-text">
							<input
								type="checkbox"
								checked={types.includes(type)}
								onChange={(event) => toggle(type, event.target.checked)}
							/>
							{notificationTypeLabel(type)}
						</label>
					))}
		</fieldset>
	)
}

interface PolicyProps {
	draft: SmsSettingsDraft
	errors: SmsSettingsFieldErrors
	update: (patch: Partial<SmsSettingsDraft>) => void
}

/** The sender and the send policy: allowed countries, the monthly cap and the notification types. */
export function SenderAndPolicyFields({ draft, errors, update }: PolicyProps) {
	return (
		<>
			<SettingsInput
				label="Messaging Service SID"
				path="fromIdentity"
				description="MG… — required with your own Twilio account."
				errors={errors}
				value={draft.fromIdentity}
				onChange={(fromIdentity) => update({ fromIdentity })}
			/>
			<SettingsInput
				label="Allowed countries"
				path="allowedCountries"
				description="Comma-separated ISO codes, e.g. ES, PT. Empty allows any country."
				errors={errors}
				value={draft.countries}
				onChange={(countries) => update({ countries })}
			/>
			<SettingsInput
				label="Monthly spend cap (EUR)"
				path="monthlySpendCapEur"
				type="number"
				description="Whole euros. Empty means no cap."
				errors={errors}
				value={draft.cap}
				onChange={(cap) => update({ cap })}
			/>
			<NotificationTypeFields types={draft.types} errors={errors} onChange={(types) => update({ types })} />
		</>
	)
}

interface ConfirmProps {
	isOpen: boolean
	onCancel: () => void
	onConfirm: () => void
}

/** Switching a BYO tenant to Managed deletes its stored credentials on save. */
export function ConfirmManagedDialog({ isOpen, onCancel, onConfirm }: ConfirmProps) {
	return (
		<Dialog isOpen={isOpen} onOpenChange={(open) => (open ? undefined : onCancel())} title="Switch to Managed?">
			<div className="flex flex-col gap-4">
				<p>Saving with Managed deletes the stored Twilio credentials. SMS will be sent through TODAY's account.</p>
				<div className="flex justify-end gap-2">
					<Button variant="muted" onPress={onCancel}>
						Cancel
					</Button>
					<Button variant="primary" onPress={onConfirm}>
						Switch to Managed
					</Button>
				</div>
			</div>
		</Dialog>
	)
}
